// Rider-side geocode client for the route popup.
//
// The public webchat is anonymous and only talks to the agents service (never movorra-api directly),
// so these hit the agents proxies under /charter/locations/* — which forward to Java with the auth it
// expects. No auth header: those proxy paths are JWT-excluded, same as the /runs endpoint the chat
// already uses.
//
// THREE calls, and which one you use matters for cost:
//   resolveLocation(q)                  -> /resolve  one-shot free text -> full candidates. One hop,
//                                          but a full (billed) search per call. Use for a CITY picker
//                                          or anywhere you need city/state without a user selection.
//   suggestLocations(input, token)      -> /suggest  Autocomplete predictions for partial input.
//                                          FREE per keystroke inside a session. Use for type-ahead.
//   fetchPlaceDetails(placeId, token)   -> /details  expands the pick and CLOSES the session; the one
//                                          billed call of a typing burst.
//
// The session token is the billing contract: mint ONE per "rider edits this field" burst, send it on
// every suggest call AND on the closing details call. Skipping it still works but bills each keystroke
// separately (~10x). newSessionToken() below mints one.
//
// Best-effort throughout: [] (or null for details) on any error, so a geocode hiccup degrades the
// popup to plain typing rather than surfacing an error to the rider.

const AGENT_API_BASE = process.env.REACT_APP_AGENT_API_URL || 'http://localhost:8000';

// A per-editing-burst opaque token. crypto.randomUUID is unavailable on old/insecure-origin browsers,
// so fall back to a random string rather than sending nothing (which would silently cost ~10x).
export function newSessionToken() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {
    /* fall through */
  }
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

// Resolve free text -> verified candidates. Each candidate:
//   { placeId, name, formattedAddress, latitude, longitude, city, state,
//     postalCode, countryCode, isAirport, iataCode }
export async function resolveLocation(query) {
  const q = (query || '').trim();
  if (!q) return [];
  try {
    const res = await fetch(`${AGENT_API_BASE}/charter/locations/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: q }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

// Type-ahead predictions for PARTIAL input. Each row: { placeId, mainText, secondaryText, fullText }.
// Deliberately has NO coordinates or city/state — that is what makes the keystrokes free. Call
// fetchPlaceDetails() with the picked placeId and the SAME token to get the full place.
export async function suggestLocations(input, sessionToken) {
  const q = (input || '').trim();
  if (!q) return [];
  try {
    const res = await fetch(`${AGENT_API_BASE}/charter/locations/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ input: q, sessionToken }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

// Expand a picked suggestion into one verified place (the same candidate shape resolveLocation
// returns, so toPlace() applies unchanged). Pass the token used for suggestLocations — that pairing
// closes the Google session and makes the whole burst bill as one lookup. null when unresolvable, so
// the caller can fall back to free text instead of locking in a half-resolved place.
export async function fetchPlaceDetails(placeId, sessionToken) {
  const id = (placeId || '').trim();
  if (!id) return null;
  try {
    const res = await fetch(`${AGENT_API_BASE}/charter/locations/details`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId: id, sessionToken }),
    });
    if (!res.ok) return null;
    return (await res.json()) || null;
  } catch (e) {
    return null;
  }
}
